import { useCallback, useEffect, useState } from 'react';
import {
  slaRules,
  ticketCategories,
  automationRules,
  businessHours,
  escalationMatrix,
  type SLARule,
  type TicketCategory,
  type AutomationRule,
  type BusinessHours,
  type EscalationMatrix,
} from '@sd/lib/adminMockData';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Clock, Edit2, ArrowUpRight, Calendar, Trash2, Users } from 'lucide-react';
import { AdminExternalUserAccess } from '@sd/components/admin/AdminExternalUserAccess';
import { toast } from 'sonner';
import { apiClient } from '@sd/services/apiClient';
import { removeMetaFields } from '@/lib/removeMetaFields';
import { formatWorkingDaysForGrid } from '@/lib/formatWorkingDaysForGrid';
import { TenantRoleService } from '@/services/APIs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DeleteConfirmDialog } from '@sd/components/common/DeleteConfirmDialog';

type UnknownRecord = Record<string, unknown>;

type DeleteRecordTarget = {
  modelName: string;
  id: string;
  label: string;
  onSuccess: () => Promise<void>;
};

const MODEL_SLA_POLICY = 'SLA Policy';
const MODEL_SLA_ESCALATION = 'SLA Escalation Rules';
const MODEL_SLA_BUSINESS_HOURS = 'SLA Business Hours';

function toArrayPayload(raw: unknown): UnknownRecord[] {
  if (Array.isArray(raw)) return raw as UnknownRecord[];
  if (!raw || typeof raw !== 'object') return [];
  const payload = raw as UnknownRecord;
  const bucketKeys = ['grid_data', 'results', 'data', 'items', 'records', 'rows'];
  for (const key of bucketKeys) {
    const val = payload[key];
    if (Array.isArray(val)) return val as UnknownRecord[];
  }
  return [];
}

function asText(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) return fallback;
  const parsed = String(value).trim();
  return parsed || fallback;
}

function asBool(value: unknown, fallback = true): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const parsed = value.trim().toLowerCase();
    if (parsed === 'true' || parsed === '1' || parsed === 'yes') return true;
    if (parsed === 'false' || parsed === '0' || parsed === 'no') return false;
  }
  if (typeof value === 'number') return value === 1;
  return fallback;
}

function pickApiKey(source: UnknownRecord, candidates: string[]): string {
  return candidates.find(k => Object.prototype.hasOwnProperty.call(source, k)) ?? candidates[0];
}

function formatTimeForApi(value: string, source: UnknownRecord, candidates: string[]): string {
  if (!value) return value;
  const key = pickApiKey(source, candidates);
  const existing = asText(source[key]);
  if (/^\d{2}:\d{2}$/.test(value) && /^\d{2}:\d{2}:\d{2}$/.test(existing)) {
    return `${value}:00`;
  }
  return value;
}

async function fetchFormilyGrid(modelName: string): Promise<UnknownRecord[]> {
  const response = await apiClient.get(
    `/formily/dynamic-model/${encodeURIComponent(modelName)}/grid_data/`,
    { params: { page: 1, page_size: 10 } }
  );
  return toArrayPayload(response?.data);
}

function formilyRecordUrl(modelName: string, id: string): string {
  return `/formily/dynamic-model/${encodeURIComponent(modelName)}/${encodeURIComponent(id)}/`;
}

/** Reject mock/fallback ids generated when API row has no uuid. */
function isPersistedRecordId(id: string): boolean {
  return Boolean(id) && !/^(sla-rule-|esc-rule-|bh-|cat-)/.test(id);
}

async function updateFormilyRecord(
  modelName: string,
  id: string,
  fields: Array<{ candidates: string[]; value: unknown }>
) {
  if (!isPersistedRecordId(id)) {
    throw new Error('Invalid record id');
  }
  const { data: current } = await apiClient.get(formilyRecordUrl(modelName, id));
  if (!current) throw new Error('Record not found');
  const source = current as UnknownRecord;
  const base = removeMetaFields(current) as UnknownRecord;
  const patch: UnknownRecord = {};
  for (const { candidates, value } of fields) {
    patch[pickApiKey(source, candidates)] = value;
  }
  const { data: updated } = await apiClient.patch(formilyRecordUrl(modelName, id), {
    ...base,
    ...patch,
  });
  return updated;
}

async function deleteFormilyRecord(modelName: string, id: string) {
  if (!isPersistedRecordId(id)) {
    throw new Error('Invalid record id');
  }
  await apiClient.delete(formilyRecordUrl(modelName, id));
}

function formatTimeForGrid(value: unknown): string {
  const text = asText(value);
  if (!text) return '—';
  const match = text.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (match) {
    const hh = match[1].padStart(2, '0');
    const mm = match[2];
    const ss = match[3];
    return ss != null ? `${hh}:${mm}:${ss}` : `${hh}:${mm}`;
  }
  return text;
}

function mapEscalationFromApi(row: UnknownRecord, index: number): EscalationMatrix {
  const id = asText(row.id ?? row.uuid ?? row.pk, `esc-rule-${index}`);

  return {
    id,
    slaPriority: asText(row.sla_priority ?? row.slaPriority),
    slaStatus: asText(row.sla_status ?? row.slaStatus),
    escalationTime: asText(row.escalation_time ?? row.escalationTime),
    time: formatTimeForGrid(row.time),
    actionType: asText(row.action_type ?? row.actionType),
    notifyRole: asText(row.notify_role ?? row.notifyRole),
    enabled: asBool(row.enabled ?? row.is_active ?? row.active, true),
  };
}

function escalationDeleteLabel(rule: EscalationMatrix): string {
  const parts = [rule.slaStatus, rule.escalationTime, rule.time].filter(Boolean);
  return parts.length > 0 ? parts.join(' / ') : rule.id;
}

function resolveRoleName(roleId: string, roleNameById: Record<string, string>): string {
  if (!roleId) return '—';
  return roleNameById[roleId] ?? roleId;
}

function normalizeTimeForInput(value: unknown): string {
  const text = asText(value);
  if (!text) return '';
  const match = text.match(/(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (match) {
    return `${match[1].padStart(2, '0')}:${match[2]}`;
  }
  return text;
}

function workingDaysToApiValue(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return trimmed;
  if (trimmed.startsWith('[')) return trimmed;
  const tokens = trimmed.split(/[,\s]+/).map(t => t.trim()).filter(Boolean);
  if (tokens.length === 0) return trimmed;
  return `['${tokens.join("','")}']`;
}

function mapBusinessHoursFromApi(row: UnknownRecord, index: number): BusinessHours {
  const id = asText(row.id ?? row.uuid ?? row.pk, `bh-${index}`);
  const workingDaysRaw = row.working_days ?? row.workingDays ?? row.workdays;
  const workingDays = formatWorkingDaysForGrid(workingDaysRaw);
  const startTime = normalizeTimeForInput(
    row.start_time ?? row.startTime ?? row.from_time ?? row.open_time ?? row.start
  );
  const endTime = normalizeTimeForInput(
    row.end_time ?? row.endTime ?? row.to_time ?? row.close_time ?? row.end
  );
  const enabled = asBool(
    row.enabled ?? row.is_active ?? row.active ?? row.working_day ?? row.is_working_day,
    true
  );

  return { id, workingDays, startTime, endTime, enabled };
}

function mapSlaRuleFromApi(row: UnknownRecord, index: number): SLARule {
  const id = asText(row.id ?? row.uuid ?? row.pk, `sla-rule-${index}`);
  const priority = asText(
    row.priority ??
      row.priority_name ??
      row.priority_label ??
      row.ticket_priority ??
      row.level,
    `P${index + 1}`
  );
  const firstResponseTime = asText(
    row.first_response ??
      row.first_response_time ??
      row.firstResponse ??
      row.firstResponseTime ??
      row.response_time,
    '-'
  );
  const resolutionTime = asText(
    row.resolution_time ?? row.resolutionTime ?? row.resolve_in ?? row.target_resolution,
    '-'
  );
  const escalationAfter = asText(
    row.escalation_after ?? row.escalationAfter ?? row.escalate_after ?? row.escalation_time,
    '-'
  );
  const enabled = asBool(row.enabled ?? row.is_active ?? row.active, true);

  return {
    id,
    priority,
    firstResponseTime,
    resolutionTime,
    escalationAfter,
    enabled,
  };
}

function RowActions({
  onEdit,
  onDelete,
  disabled,
}: {
  onEdit: () => void;
  onDelete: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex shrink-0 items-center gap-1">
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit} disabled={disabled}>
        <Edit2 className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-destructive hover:text-destructive"
        onClick={onDelete}
        disabled={disabled}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

function SettingsTabsList({ children }: { children: React.ReactNode }) {
  return (
    <div className="-mx-4 overflow-x-auto overscroll-x-contain px-4 pb-1 sm:mx-0 sm:px-0">
      <TabsList className="inline-flex h-auto w-max max-w-none flex-nowrap gap-1">
        {children}
      </TabsList>
    </div>
  );
}

function SettingsTableWrap({
  children,
  minWidthClass = "min-w-[32rem]",
  className = "",
}: {
  children: React.ReactNode;
  minWidthClass?: string;
  className?: string;
}) {
  return (
    <div
      className={`-mx-1 overflow-x-auto overscroll-x-contain sm:mx-0 ${className}`.trim()}
    >
      <div className={minWidthClass}>{children}</div>
    </div>
  );
}

function EscalationRuleMobileCard({
  esc,
  resolvedPriority,
  notifyRoleLabel,
  disabled,
  onEdit,
  onDelete,
}: {
  esc: EscalationMatrix;
  resolvedPriority: string;
  notifyRoleLabel: string;
  disabled?: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            SLA Priority
          </p>
          <p className="truncate font-semibold text-foreground" title={resolvedPriority}>
            {resolvedPriority || "—"}
          </p>
        </div>
        <RowActions disabled={disabled} onEdit={onEdit} onDelete={onDelete} />
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <div>
          <dt className="text-xs text-muted-foreground">SLA Status</dt>
          <dd className="font-medium">{esc.slaStatus || "—"}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Escalation Time</dt>
          <dd className="font-medium">{esc.escalationTime || "—"}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Time</dt>
          <dd className="font-medium">{esc.time || "—"}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Action Type</dt>
          <dd className="font-medium">{esc.actionType || "—"}</dd>
        </div>
        <div className="col-span-2">
          <dt className="text-xs text-muted-foreground">Notify Role</dt>
          <dd className="truncate font-medium" title={notifyRoleLabel}>
            {notifyRoleLabel}
          </dd>
        </div>
      </dl>
    </div>
  );
}

export function AdminSettings() {
  const [slaConfig, setSlaConfig] = useState<SLARule[]>(slaRules);
  const [categories, setCategories] = useState<TicketCategory[]>(ticketCategories);
  const [automations, setAutomations] = useState<AutomationRule[]>(automationRules);
  const [hours, setHours] = useState<BusinessHours[]>(businessHours);
  const [escMatrix, setEscMatrix] = useState<EscalationMatrix[]>(escalationMatrix);
  const [editSla, setEditSla] = useState<{ open: boolean; rule: SLARule | null }>({ open: false, rule: null });
  const [editEscalation, setEditEscalation] = useState<{ open: boolean; rule: EscalationMatrix | null }>({ open: false, rule: null });
  const [editHours, setEditHours] = useState<{ open: boolean; row: BusinessHours | null }>({ open: false, row: null });
  const [editCategory, setEditCategory] = useState<{ open: boolean; category: TicketCategory | null; isNew: boolean }>({ open: false, category: null, isNew: false });
  const [slaForm, setSlaForm] = useState({
    priority: '',
    firstResponseTime: '',
    resolutionTime: '',
    escalationAfter: '',
    enabled: true,
  });
  const [escalationForm, setEscalationForm] = useState({
    slaPriority: '',
    slaStatus: '',
    escalationTime: '',
    time: '',
    actionType: '',
    notifyRole: '',
    enabled: true,
  });
  const [hoursForm, setHoursForm] = useState({
    workingDays: '',
    startTime: '',
    endTime: '',
    enabled: true,
  });
  const [catForm, setCatForm] = useState({ name: '', description: '' });
  const [activeTab, setActiveTab] = useState('sla');
  const [isSlaLoading, setIsSlaLoading] = useState(false);
  const [slaLoadError, setSlaLoadError] = useState<string | null>(null);
  const [isEscalationLoading, setIsEscalationLoading] = useState(false);
  const [escalationLoadError, setEscalationLoadError] = useState<string | null>(null);
  const [escalationLoaded, setEscalationLoaded] = useState(false);
  const [isHoursLoading, setIsHoursLoading] = useState(false);
  const [hoursLoadError, setHoursLoadError] = useState<string | null>(null);
  const [hoursLoaded, setHoursLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roleNameById, setRoleNameById] = useState<Record<string, string>>({});
  const [deleteTarget, setDeleteTarget] = useState<DeleteRecordTarget | null>(null);
  const [deleteConfirming, setDeleteConfirming] = useState(false);

  const loadSlaPolicy = useCallback(async () => {
    setIsSlaLoading(true);
    setSlaLoadError(null);
    try {
      const rows = await fetchFormilyGrid(MODEL_SLA_POLICY);
      if (rows.length === 0) {
        setSlaConfig([]);
        setSlaLoadError('No SLA policy records found');
        return;
      }
      setSlaConfig(rows.map(mapSlaRuleFromApi));
    } catch {
      setSlaLoadError('Failed to load SLA policy data');
    } finally {
      setIsSlaLoading(false);
    }
  }, []);

  const loadServiceDeskRoles = useCallback(async () => {
    try {
      const response = await TenantRoleService.listRoles(1, 100);
      const map: Record<string, string> = {};
      for (const role of response.results ?? []) {
        map[role.id] = role.name;
      }
      setRoleNameById(map);
    } catch {
      setRoleNameById({});
    }
  }, []);

  const loadEscalationRules = useCallback(async () => {
    setIsEscalationLoading(true);
    setEscalationLoadError(null);
    setEscMatrix([]);
    try {
      const rows = await fetchFormilyGrid(MODEL_SLA_ESCALATION);
      if (rows.length === 0) {
        setEscMatrix([]);
        setEscalationLoadError('No SLA escalation rules found');
        return;
      }
      setEscMatrix(rows.map(mapEscalationFromApi));
    } catch {
      setEscalationLoadError('Failed to load SLA escalation rules');
    } finally {
      setIsEscalationLoading(false);
    }
  }, []);

  const loadBusinessHours = useCallback(async () => {
    setIsHoursLoading(true);
    setHoursLoadError(null);
    setHours([]);
    try {
      const rows = await fetchFormilyGrid(MODEL_SLA_BUSINESS_HOURS);
      if (rows.length === 0) {
        setHours([]);
        setHoursLoadError('No SLA business hours found');
        return;
      }
      setHours(rows.map(mapBusinessHoursFromApi));
    } catch {
      setHoursLoadError('Failed to load SLA business hours');
    } finally {
      setIsHoursLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSlaPolicy();
  }, [loadSlaPolicy]);

  useEffect(() => {
    if (activeTab !== 'escalation' || escalationLoaded) return;
    void Promise.all([loadEscalationRules(), loadServiceDeskRoles()]).then(() =>
      setEscalationLoaded(true)
    );
  }, [activeTab, escalationLoaded, loadEscalationRules, loadServiceDeskRoles]);

  useEffect(() => {
    if (activeTab !== 'hours' || hoursLoaded) return;
    void loadBusinessHours().then(() => setHoursLoaded(true));
  }, [activeTab, hoursLoaded, loadBusinessHours]);

  const requestDeleteRecord = (
    modelName: string,
    id: string,
    label: string,
    onSuccess: () => Promise<void>
  ) => {
    if (!isPersistedRecordId(id)) {
      toast.error('Cannot delete: record id missing from API');
      return;
    }
    setDeleteTarget({ modelName, id, label, onSuccess });
  };

  const confirmDeleteRecord = async () => {
    if (!deleteTarget) return;
    setDeleteConfirming(true);
    setIsSubmitting(true);
    try {
      await deleteFormilyRecord(deleteTarget.modelName, deleteTarget.id);
      toast.success('Deleted successfully');
      await deleteTarget.onSuccess();
      setDeleteTarget(null);
    } catch {
      toast.error('Failed to delete record');
    } finally {
      setDeleteConfirming(false);
      setIsSubmitting(false);
    }
  };

  const openSlaEdit = async (rule: SLARule) => {
    if (!isPersistedRecordId(rule.id)) {
      toast.error('Cannot edit: record id missing from API');
      return;
    }
    let mapped = rule;
    try {
      const { data } = await apiClient.get(formilyRecordUrl(MODEL_SLA_POLICY, rule.id));
      if (data) mapped = mapSlaRuleFromApi(data as UnknownRecord, 0);
    } catch {
      // use grid row values if detail fetch fails
    }
    setSlaForm({
      priority: mapped.priority,
      firstResponseTime: mapped.firstResponseTime,
      resolutionTime: mapped.resolutionTime,
      escalationAfter: mapped.escalationAfter,
      enabled: mapped.enabled,
    });
    setEditSla({ open: true, rule: mapped });
  };

  const saveSlaEdit = async () => {
    if (!editSla.rule) return;
    if (!isPersistedRecordId(editSla.rule.id)) {
      toast.error('Cannot save: record id missing from API');
      return;
    }
    setIsSubmitting(true);
    try {
      const { data: current } = await apiClient.get(formilyRecordUrl(MODEL_SLA_POLICY, editSla.rule.id));
      const source = (current ?? {}) as UnknownRecord;
      await updateFormilyRecord(MODEL_SLA_POLICY, editSla.rule.id, [
        { candidates: ['priority', 'priority_name', 'ticket_priority'], value: slaForm.priority },
        {
          candidates: ['first_response', 'first_response_time', 'response_time'],
          value: formatTimeForApi(slaForm.firstResponseTime, source, ['first_response', 'first_response_time', 'response_time']),
        },
        {
          candidates: ['resolution_time', 'resolve_in', 'target_resolution'],
          value: formatTimeForApi(slaForm.resolutionTime, source, ['resolution_time', 'resolve_in', 'target_resolution']),
        },
        {
          candidates: ['escalation_after', 'escalate_after', 'escalation_time'],
          value: formatTimeForApi(slaForm.escalationAfter, source, ['escalation_after', 'escalate_after', 'escalation_time']),
        },
        { candidates: ['enabled', 'is_active', 'active'], value: slaForm.enabled },
      ]);
      toast.success(`SLA rule for ${slaForm.priority} updated`);
      setEditSla({ open: false, rule: null });
      await loadSlaPolicy();
    } catch {
      toast.error('Failed to update SLA policy');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEscalationEdit = async (rule: EscalationMatrix) => {
    if (!isPersistedRecordId(rule.id)) {
      toast.error('Cannot edit: record id missing from API');
      return;
    }
    let mapped = rule;
    try {
      const { data } = await apiClient.get(formilyRecordUrl(MODEL_SLA_ESCALATION, rule.id));
      if (data) mapped = mapEscalationFromApi(data as UnknownRecord, 0);
    } catch {
      // use grid row values if detail fetch fails
    }
    setEscalationForm({
      slaPriority: mapped.slaPriority,
      slaStatus: mapped.slaStatus,
      escalationTime: mapped.escalationTime,
      time: mapped.time === '—' ? '' : mapped.time,
      actionType: mapped.actionType,
      notifyRole: mapped.notifyRole,
      enabled: mapped.enabled,
    });
    setEditEscalation({ open: true, rule: mapped });
  };

  const saveEscalationEdit = async () => {
    if (!editEscalation.rule) return;
    if (!isPersistedRecordId(editEscalation.rule.id)) {
      toast.error('Cannot save: record id missing from API');
      return;
    }
    setIsSubmitting(true);
    try {
      const { data: current } = await apiClient.get(
        formilyRecordUrl(MODEL_SLA_ESCALATION, editEscalation.rule.id)
      );
      const source = (current ?? {}) as UnknownRecord;
      await updateFormilyRecord(MODEL_SLA_ESCALATION, editEscalation.rule.id, [
        { candidates: ['sla_priority', 'slaPriority'], value: escalationForm.slaPriority },
        { candidates: ['sla_status', 'slaStatus'], value: escalationForm.slaStatus },
        { candidates: ['escalation_time', 'escalationTime'], value: escalationForm.escalationTime },
        {
          candidates: ['time'],
          value: formatTimeForApi(escalationForm.time, source, ['time']),
        },
        { candidates: ['action_type', 'actionType'], value: escalationForm.actionType },
        { candidates: ['notify_role', 'notifyRole'], value: escalationForm.notifyRole },
        { candidates: ['enabled', 'is_active', 'active'], value: escalationForm.enabled },
      ]);
      toast.success('Escalation rule updated');
      setEditEscalation({ open: false, rule: null });
      await loadEscalationRules();
    } catch {
      toast.error('Failed to update escalation rule');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openHoursEdit = async (row: BusinessHours) => {
    if (!isPersistedRecordId(row.id)) {
      toast.error('Cannot edit: record id missing from API');
      return;
    }
    let mapped = row;
    try {
      const { data } = await apiClient.get(formilyRecordUrl(MODEL_SLA_BUSINESS_HOURS, row.id));
      if (data) mapped = mapBusinessHoursFromApi(data as UnknownRecord, 0);
    } catch {
      // use grid row values if detail fetch fails
    }
    setHoursForm({
      workingDays: mapped.workingDays,
      startTime: mapped.startTime,
      endTime: mapped.endTime,
      enabled: mapped.enabled,
    });
    setEditHours({ open: true, row: mapped });
  };

  const saveHoursEdit = async () => {
    if (!editHours.row) return;
    if (!isPersistedRecordId(editHours.row.id)) {
      toast.error('Cannot save: record id missing from API');
      return;
    }
    setIsSubmitting(true);
    try {
      const { data: current } = await apiClient.get(
        formilyRecordUrl(MODEL_SLA_BUSINESS_HOURS, editHours.row.id)
      );
      const source = (current ?? {}) as UnknownRecord;
      await updateFormilyRecord(MODEL_SLA_BUSINESS_HOURS, editHours.row.id, [
        {
          candidates: ['working_days', 'workingDays', 'workdays'],
          value: workingDaysToApiValue(hoursForm.workingDays),
        },
        {
          candidates: ['start_time', 'from_time', 'open_time', 'start'],
          value: formatTimeForApi(hoursForm.startTime, source, ['start_time', 'from_time', 'open_time', 'start']),
        },
        {
          candidates: ['end_time', 'to_time', 'close_time', 'end'],
          value: formatTimeForApi(hoursForm.endTime, source, ['end_time', 'to_time', 'close_time', 'end']),
        },
        { candidates: ['enabled', 'is_active', 'active', 'working_day', 'is_working_day'], value: hoursForm.enabled },
      ]);
      toast.success('Business hours updated');
      setEditHours({ open: false, row: null });
      await loadBusinessHours();
    } catch {
      toast.error('Failed to update business hours');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCatEdit = (cat: TicketCategory | null) => {
    if (cat) {
      setCatForm({ name: cat.name, description: cat.description });
      setEditCategory({ open: true, category: cat, isNew: false });
    } else {
      setCatForm({ name: '', description: '' });
      setEditCategory({ open: true, category: null, isNew: true });
    }
  };

  const saveCatEdit = () => {
    if (editCategory.isNew) {
      const newCat: TicketCategory = { id: `cat-${Date.now()}`, name: catForm.name, description: catForm.description, ticketCount: 0, enabled: true };
      setCategories(prev => [...prev, newCat]);
      toast.success(`Category "${catForm.name}" created`);
    } else if (editCategory.category) {
      setCategories(prev => prev.map(c => c.id === editCategory.category!.id ? { ...c, name: catForm.name, description: catForm.description } : c));
      toast.success(`Category "${catForm.name}" updated`);
    }
    setEditCategory({ open: false, category: null, isNew: false });
  };

  const toggleCategory = (catId: string) => setCategories(prev => prev.map(c => c.id === catId ? { ...c, enabled: !c.enabled } : c));
  const toggleAutomation = (id: string) => setAutomations(prev => prev.map(a => a.id === id ? { ...a, enabled: !a.enabled } : a));

  return (
    <div className="min-w-0 space-y-4 p-4 sm:space-y-6 sm:p-6">
      <div className="min-w-0">
        <h1 className="text-xl font-bold text-foreground sm:text-2xl">Settings & Configuration</h1>
        <p className="text-sm text-muted-foreground sm:text-base">
          Configure SLA, automation, escalation, categories and more
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="min-w-0 space-y-4 sm:space-y-6">
        <SettingsTabsList>
          <TabsTrigger value="sla" className="shrink-0 gap-1.5 text-xs sm:text-sm">
            <Clock className="h-4 w-4 shrink-0" /> SLA Policy
          </TabsTrigger>
          <TabsTrigger value="escalation" className="shrink-0 gap-1.5 text-xs sm:text-sm">
            <ArrowUpRight className="h-4 w-4 shrink-0" /> SLA Escalation Rules
          </TabsTrigger>
          <TabsTrigger value="hours" className="shrink-0 gap-1.5 text-xs sm:text-sm">
            <Calendar className="h-4 w-4 shrink-0" /> SLA Business Hours
          </TabsTrigger>
          <TabsTrigger value="external-user-access" className="shrink-0 gap-1.5 text-xs sm:text-sm">
            <Users className="h-4 w-4 shrink-0" /> External User Access
          </TabsTrigger>
        </SettingsTabsList>

        <TabsContent value="sla">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">SLA Policy</CardTitle>
              <CardDescription>Configure SLA targets for each priority level</CardDescription>
              {isSlaLoading ? <p className="text-xs text-muted-foreground">Loading SLA policy from API...</p> : null}
              {slaLoadError ? <p className="text-xs text-destructive">{slaLoadError}</p> : null}
            </CardHeader>
            <CardContent className="min-w-0">
              <SettingsTableWrap minWidthClass="min-w-[28rem]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Priority</TableHead>
                      <TableHead>First Response</TableHead>
                      <TableHead>Resolution Time</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {slaConfig.map((rule) => (
                      <TableRow key={rule.id}>
                        <TableCell className="font-medium">{rule.priority}</TableCell>
                        <TableCell>{rule.firstResponseTime}</TableCell>
                        <TableCell>{rule.resolutionTime}</TableCell>
                        <TableCell>
                          <RowActions
                            disabled={isSubmitting}
                            onEdit={() => void openSlaEdit(rule)}
                            onDelete={() =>
                              requestDeleteRecord(MODEL_SLA_POLICY, rule.id, rule.priority, loadSlaPolicy)
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </SettingsTableWrap>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="escalation">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">SLA Escalation Rules</CardTitle>
              <CardDescription>Escalation rules by priority, status, and notify settings</CardDescription>
              {isEscalationLoading ? <p className="text-xs text-muted-foreground">Loading escalation rules from API...</p> : null}
              {escalationLoadError ? <p className="text-xs text-destructive">{escalationLoadError}</p> : null}
            </CardHeader>
            <CardContent className="min-w-0 space-y-4">
              <div className="space-y-3 md:hidden">
                {!isEscalationLoading && escMatrix.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    No escalation rules to display
                  </p>
                ) : null}
                {escMatrix.map(esc => {
                  const resolvedPriority =
                    slaConfig.find(r => r.id === esc.slaPriority)?.priority || esc.slaPriority;
                  const notifyRoleLabel = resolveRoleName(esc.notifyRole, roleNameById);
                  return (
                    <EscalationRuleMobileCard
                      key={esc.id}
                      esc={esc}
                      resolvedPriority={resolvedPriority}
                      notifyRoleLabel={notifyRoleLabel}
                      disabled={isSubmitting}
                      onEdit={() => void openEscalationEdit(esc)}
                      onDelete={() =>
                        requestDeleteRecord(
                          MODEL_SLA_ESCALATION,
                          esc.id,
                          escalationDeleteLabel(esc),
                          loadEscalationRules
                        )
                      }
                    />
                  );
                })}
              </div>

              <SettingsTableWrap minWidthClass="min-w-[44rem]" className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SLA Priority</TableHead>
                      <TableHead>SLA Status</TableHead>
                      <TableHead>Escalation Time</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Action Type</TableHead>
                      <TableHead>Notify Role</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!isEscalationLoading && escMatrix.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                          No escalation rules to display
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {escMatrix.map(esc => {
                      const resolvedPriority =
                        slaConfig.find(r => r.id === esc.slaPriority)?.priority || esc.slaPriority;
                      return (
                        <TableRow key={esc.id}>
                          <TableCell className="max-w-[140px] truncate font-medium" title={resolvedPriority}>
                            {resolvedPriority || "—"}
                          </TableCell>
                          <TableCell>{esc.slaStatus || "—"}</TableCell>
                          <TableCell>{esc.escalationTime || "—"}</TableCell>
                          <TableCell>{esc.time || "—"}</TableCell>
                          <TableCell>{esc.actionType || "—"}</TableCell>
                          <TableCell
                            className="max-w-[140px] truncate"
                            title={resolveRoleName(esc.notifyRole, roleNameById)}
                          >
                            {resolveRoleName(esc.notifyRole, roleNameById)}
                          </TableCell>
                          <TableCell>
                            <RowActions
                              disabled={isSubmitting}
                              onEdit={() => void openEscalationEdit(esc)}
                              onDelete={() =>
                                requestDeleteRecord(
                                  MODEL_SLA_ESCALATION,
                                  esc.id,
                                  escalationDeleteLabel(esc),
                                  loadEscalationRules
                                )
                              }
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </SettingsTableWrap>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="hours">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">SLA Business Hours</CardTitle>
              <CardDescription>Define operating hours for SLA calculation</CardDescription>
              {isHoursLoading ? <p className="text-xs text-muted-foreground">Loading business hours from API...</p> : null}
              {hoursLoadError ? <p className="text-xs text-destructive">{hoursLoadError}</p> : null}
            </CardHeader>
            <CardContent className="min-w-0">
              <SettingsTableWrap minWidthClass="min-w-[28rem]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Working Day</TableHead>
                      <TableHead>Start Time</TableHead>
                      <TableHead>End Time</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!isHoursLoading && hours.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                          No business hours to display
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {hours.map(h => (
                      <TableRow key={h.id}>
                        <TableCell className="font-medium">{h.workingDays || "—"}</TableCell>
                        <TableCell>{h.startTime || "—"}</TableCell>
                        <TableCell>{h.endTime || "—"}</TableCell>
                        <TableCell>
                          <RowActions
                            disabled={isSubmitting}
                            onEdit={() => void openHoursEdit(h)}
                            onDelete={() =>
                              requestDeleteRecord(
                                MODEL_SLA_BUSINESS_HOURS,
                                h.id,
                                h.workingDays,
                                loadBusinessHours
                              )
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </SettingsTableWrap>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="external-user-access">
          <AdminExternalUserAccess enabled={activeTab === "external-user-access"} />
        </TabsContent>
      </Tabs>

      <Dialog open={editSla.open} onOpenChange={(open) => !open && setEditSla({ open: false, rule: null })}>
        <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit SLA Rule — {editSla.rule?.priority}</DialogTitle>
            <DialogDescription>Update response and resolution times</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Priority</Label>
              <Input value={slaForm.priority} onChange={(e) => setSlaForm(prev => ({ ...prev, priority: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>First Response Time</Label>
              <Input value={slaForm.firstResponseTime} onChange={(e) => setSlaForm(prev => ({ ...prev, firstResponseTime: e.target.value }))} placeholder="00:15:00" />
            </div>
            <div className="space-y-2">
              <Label>Resolution Time</Label>
              <Input value={slaForm.resolutionTime} onChange={(e) => setSlaForm(prev => ({ ...prev, resolutionTime: e.target.value }))} placeholder="04:00:00" />
            </div>
            <div className="space-y-2">
              <Label>Escalation After</Label>
              <Input value={slaForm.escalationAfter} onChange={(e) => setSlaForm(prev => ({ ...prev, escalationAfter: e.target.value }))} placeholder="00:30:00" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={slaForm.enabled} onCheckedChange={(checked) => setSlaForm(prev => ({ ...prev, enabled: checked }))} />
              <Label>Enabled</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSla({ open: false, rule: null })} disabled={isSubmitting}>Cancel</Button>
            <Button onClick={() => void saveSlaEdit()} disabled={isSubmitting}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editEscalation.open} onOpenChange={(open) => !open && setEditEscalation({ open: false, rule: null })}>
        <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Escalation Rule</DialogTitle>
            <DialogDescription>Update escalation rule fields</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>SLA Priority</Label>
              <Input value={escalationForm.slaPriority} onChange={(e) => setEscalationForm(prev => ({ ...prev, slaPriority: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>SLA Status</Label>
              <Input value={escalationForm.slaStatus} onChange={(e) => setEscalationForm(prev => ({ ...prev, slaStatus: e.target.value }))} placeholder="on_track" />
            </div>
            <div className="space-y-2">
              <Label>Escalation Time</Label>
              <Input value={escalationForm.escalationTime} onChange={(e) => setEscalationForm(prev => ({ ...prev, escalationTime: e.target.value }))} placeholder="before" />
            </div>
            <div className="space-y-2">
              <Label>Time</Label>
              <Input value={escalationForm.time} onChange={(e) => setEscalationForm(prev => ({ ...prev, time: e.target.value }))} placeholder="00:05:00" />
            </div>
            <div className="space-y-2">
              <Label>Action Type</Label>
              <Input value={escalationForm.actionType} onChange={(e) => setEscalationForm(prev => ({ ...prev, actionType: e.target.value }))} placeholder="IN_APP" />
            </div>
            <div className="space-y-2">
              <Label>Notify Role</Label>
              <Select
                value={escalationForm.notifyRole}
                onValueChange={(value) => setEscalationForm(prev => ({ ...prev, notifyRole: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(roleNameById).map(([id, name]) => (
                    <SelectItem key={id} value={id}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={escalationForm.enabled} onCheckedChange={(checked) => setEscalationForm(prev => ({ ...prev, enabled: checked }))} />
              <Label>Enabled</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEscalation({ open: false, rule: null })} disabled={isSubmitting}>Cancel</Button>
            <Button onClick={() => void saveEscalationEdit()} disabled={isSubmitting}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editHours.open} onOpenChange={(open) => !open && setEditHours({ open: false, row: null })}>
        <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Business Hours</DialogTitle>
            <DialogDescription>Update working days and operating times</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Working Day</Label>
              <Input
                value={hoursForm.workingDays}
                onChange={(e) => setHoursForm(prev => ({ ...prev, workingDays: e.target.value }))}
                placeholder="MON, TUE, WED, THU, FRI, SAT"
              />
            </div>
            <div className="space-y-2">
              <Label>Start Time</Label>
              <Input type="time" value={hoursForm.startTime} onChange={(e) => setHoursForm(prev => ({ ...prev, startTime: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>End Time</Label>
              <Input type="time" value={hoursForm.endTime} onChange={(e) => setHoursForm(prev => ({ ...prev, endTime: e.target.value }))} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={hoursForm.enabled} onCheckedChange={(checked) => setHoursForm(prev => ({ ...prev, enabled: checked }))} />
              <Label>Enabled</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditHours({ open: false, row: null })} disabled={isSubmitting}>Cancel</Button>
            <Button onClick={() => void saveHoursEdit()} disabled={isSubmitting}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editCategory.open} onOpenChange={(open) => !open && setEditCategory({ open: false, category: null, isNew: false })}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editCategory.isNew ? 'Add Category' : 'Edit Category'}</DialogTitle><DialogDescription>{editCategory.isNew ? 'Create a new ticket category' : 'Update category details'}</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input value={catForm.name} onChange={(e) => setCatForm(prev => ({ ...prev, name: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={catForm.description} onChange={(e) => setCatForm(prev => ({ ...prev, description: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditCategory({ open: false, category: null, isNew: false })}>Cancel</Button><Button onClick={saveCatEdit}>{editCategory.isNew ? 'Create' : 'Save'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={open => {
          if (!open && !deleteConfirming) setDeleteTarget(null);
        }}
        title="Delete this record?"
        description="This action is permanent and cannot be undone."
        itemCaption="Record"
        itemLabel={deleteTarget?.label ?? ''}
        warningMessage="This setting will be removed immediately and may affect SLA calculations."
        confirmButtonLabel="Delete record"
        onConfirm={confirmDeleteRecord}
        confirming={deleteConfirming}
      />
    </div>
  );
}
